import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import Tao2DocumentView from '../components/Tao2DocumentView';
import {
  TAO2_DEFAULT_FONT_ID,
  TAO2_FONTS,
  TAO2_FONT_SIZE_DEFAULT,
  TAO2_FONT_SIZE_MAX,
  TAO2_FONT_SIZE_MIN,
  TAO2_FONT_SIZE_STEP,
  TAO2_PREFS_KEY,
  ensureTao2FontsLoaded,
  getTao2FontFamilyWeb,
} from '../utils/tao2';
import { TAO2_HTML, TAO2_TOC } from './tao2Document';

const LEFT_OPEN_WIDTH = 300;

function clampFontSize(size) {
  const n = Number(size);
  if (!Number.isFinite(n)) return TAO2_FONT_SIZE_DEFAULT;
  return Math.min(TAO2_FONT_SIZE_MAX, Math.max(TAO2_FONT_SIZE_MIN, n));
}

export default function Tao2Page({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const currentUser = route?.params?.user || null;

  const [leftPanelMinimized, setLeftPanelMinimized] = useState(false);
  const [activeId, setActiveId] = useState(TAO2_TOC[0]?.id || null);
  const [scrollTarget, setScrollTarget] = useState(null);
  const [fontId, setFontId] = useState(TAO2_DEFAULT_FONT_ID);
  const [fontSize, setFontSize] = useState(TAO2_FONT_SIZE_DEFAULT);
  const [fontMenuOpen, setFontMenuOpen] = useState(false);

  const styles = getStyles(theme);
  const bannerFontFamily = getTao2FontFamilyWeb(fontId);
  const selectedFont = TAO2_FONTS.find((item) => item.id === fontId) || TAO2_FONTS[0];

  useEffect(() => {
    ensureTao2FontsLoaded();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(TAO2_PREFS_KEY);
        if (!raw || cancelled) return;
        const parsed = JSON.parse(raw);
        if (TAO2_FONTS.some((item) => item.id === parsed?.fontId)) setFontId(parsed.fontId);
        if (parsed?.fontSize != null) setFontSize(clampFontSize(parsed.fontSize));
      } catch {
        /* ignore corrupt prefs */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    AsyncStorage.setItem(TAO2_PREFS_KEY, JSON.stringify({ fontId, fontSize })).catch(() => {});
  }, [fontId, fontSize]);

  const goToSection = useCallback((id) => {
    setActiveId(id);
    setFontMenuOpen(false);
    setScrollTarget({ id, nonce: Date.now() });
  }, []);

  const bumpFont = useCallback((delta) => {
    setFontSize((prev) => clampFontSize(prev + delta));
  }, []);

  const docFontFamily = useMemo(() => getTao2FontFamilyWeb(fontId), [fontId]);

  return (
    <View
      style={styles.container}
      {...(Platform.OS === 'web'
        ? { dataSet: { screen: 'tao-2', theme: theme.isDarkMode ? 'dark' : 'light' } }
        : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View
        style={styles.header}
        {...(Platform.OS === 'web' ? { dataSet: { header: 'true' } } : {})}
      >
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} currentUser={currentUser} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Tao 2</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <View
        style={styles.toolsBanner}
        nativeID="tao2-tools-banner"
        {...(Platform.OS === 'web' ? { dataSet: { tao2Tools: 'true' } } : {})}
      >
        <Pressable
          onPress={() => setLeftPanelMinimized((v) => !v)}
          accessibilityRole="button"
          accessibilityLabel={leftPanelMinimized ? 'Show table of contents' : 'Hide table of contents'}
          {...(Platform.OS === 'web' ? { dataSet: { tao2ToolBtn: 'true' } } : {})}
          style={({ pressed }) => [styles.toolButton, styles.tocToggle, pressed && styles.toolButtonPressed]}
        >
          <Text style={[styles.toolButtonText, styles.tocToggleText]}>☰</Text>
        </Pressable>
        <View style={styles.toolsGroup}>
          <Pressable
            onPress={() => bumpFont(-TAO2_FONT_SIZE_STEP)}
            disabled={fontSize <= TAO2_FONT_SIZE_MIN}
            accessibilityRole="button"
            accessibilityLabel="Decrease font size"
            {...(Platform.OS === 'web' ? { dataSet: { tao2ToolBtn: 'true' } } : {})}
            style={({ pressed }) => [
              styles.toolButton,
              pressed && styles.toolButtonPressed,
              fontSize <= TAO2_FONT_SIZE_MIN && styles.toolButtonDisabled,
            ]}
          >
            <Text style={[styles.toolButtonText, { fontFamily: bannerFontFamily }]}>A−</Text>
          </Pressable>
          <Pressable
            onPress={() => bumpFont(TAO2_FONT_SIZE_STEP)}
            disabled={fontSize >= TAO2_FONT_SIZE_MAX}
            accessibilityRole="button"
            accessibilityLabel="Increase font size"
            {...(Platform.OS === 'web' ? { dataSet: { tao2ToolBtn: 'true' } } : {})}
            style={({ pressed }) => [
              styles.toolButton,
              pressed && styles.toolButtonPressed,
              fontSize >= TAO2_FONT_SIZE_MAX && styles.toolButtonDisabled,
            ]}
          >
            <Text style={[styles.toolButtonText, styles.toolButtonTextLg, { fontFamily: bannerFontFamily }]}>A+</Text>
          </Pressable>
        </View>
        <View style={styles.fontPickerWrap}>
          <Pressable
            onPress={() => setFontMenuOpen((open) => !open)}
            accessibilityRole="button"
            accessibilityLabel="Choose reader font"
            {...(Platform.OS === 'web' ? { dataSet: { tao2ToolBtn: 'true', tao2FontTrigger: 'true' } } : {})}
            style={({ pressed }) => [styles.fontTrigger, pressed && styles.toolButtonPressed]}
          >
            <Text style={[styles.fontTriggerText, { fontFamily: bannerFontFamily, fontSize: Math.max(13, fontSize - 3) }]}>
              {selectedFont.label}
            </Text>
            <Text style={styles.fontChevron}>{fontMenuOpen ? '▴' : '▾'}</Text>
          </Pressable>
          {fontMenuOpen ? (
            <View
              style={styles.fontMenu}
              nativeID="tao2-font-menu"
              {...(Platform.OS === 'web' ? { dataSet: { tao2FontMenu: 'true', tao2FontMenuSurface: 'true' } } : {})}
            >
              {TAO2_FONTS.map((font) => (
                <Pressable
                  key={font.id}
                  onPress={() => {
                    setFontId(font.id);
                    setFontMenuOpen(false);
                  }}
                  {...(Platform.OS === 'web' ? { dataSet: { tao2FontMenuItem: 'true' } } : {})}
                  style={({ pressed }) => [
                    styles.fontMenuItem,
                    font.id === fontId && styles.fontMenuItemActive,
                    pressed && styles.tocItemPressed,
                  ]}
                >
                  <Text
                    style={[
                      styles.fontMenuItemText,
                      { fontFamily: font.web, fontSize: Math.max(14, fontSize - 2) },
                      font.id === fontId && styles.fontMenuItemTextActive,
                    ]}
                  >
                    {font.label}
                  </Text>
                </Pressable>
              ))}
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.content}>
        {!leftPanelMinimized && (
          <View
            style={[styles.leftMenu, { width: LEFT_OPEN_WIDTH }]}
            {...(Platform.OS === 'web' ? { dataSet: { tao2LeftPane: 'true' } } : {})}
          >
            <Text style={[styles.tocHeading, { fontFamily: bannerFontFamily }]}>Table of contents</Text>
            <ScrollView
              style={styles.tocScroll}
              contentContainerStyle={styles.tocScrollContent}
              showsVerticalScrollIndicator
            >
              {TAO2_TOC.map((node) => {
                const isActive = node.id === activeId;
                return (
                  <Pressable
                    key={node.id}
                    onPress={() => goToSection(node.id)}
                    accessibilityRole="link"
                    accessibilityLabel={`Go to ${node.title}`}
                    {...(Platform.OS === 'web' ? { dataSet: { tao2TocItem: 'true' } } : {})}
                    style={({ pressed }) => [
                      styles.tocItem,
                      node.level >= 3 && styles.tocItemNested,
                      isActive && styles.tocItemActive,
                      pressed && styles.tocItemPressed,
                    ]}
                  >
                    <Text
                      style={[
                        styles.tocText,
                        node.level >= 3 ? styles.tocTextSub : styles.tocTextTop,
                        { fontFamily: bannerFontFamily },
                        isActive && styles.tocTextActive,
                      ]}
                      numberOfLines={2}
                    >
                      {node.title}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        )}

        <Tao2DocumentView
          html={TAO2_HTML}
          fontFamily={docFontFamily}
          fontSize={fontSize}
          scrollTarget={scrollTarget}
          onActive={setActiveId}
          style={styles.readerPane}
        />
      </View>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme) {
  const gold = theme.colors.heading || theme.colors.primary || '#d4af37';
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#faf8f4',
      ...(Platform.OS === 'web' ? { position: 'relative' } : {}),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 16,
      paddingHorizontal: 20,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? '#333' : '#EEE',
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    hamburgerContainer: { marginRight: 15, backgroundColor: 'transparent' },
    breadcrumbContainer: { flex: 1, backgroundColor: 'transparent' },
    breadcrumb: { fontSize: 18, fontWeight: '600', color: gold, backgroundColor: 'transparent' },
    profileIconContainer: { backgroundColor: 'transparent' },
    toolsBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? '#333' : '#E6D9B8',
      backgroundColor: theme.isDarkMode ? '#1a1610' : '#f3ead6',
      zIndex: 30,
      elevation: 12,
      overflow: 'visible',
      gap: 12,
    },
    toolsGroup: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'transparent' },
    tocToggle: { paddingHorizontal: 10, minWidth: 40, alignItems: 'center' },
    tocToggleText: { fontSize: 18, lineHeight: 20 },
    toolButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: gold,
      backgroundColor: theme.isDarkMode ? '#241e14' : '#fffaf0',
    },
    toolButtonPressed: { opacity: 0.8 },
    toolButtonDisabled: { opacity: 0.4 },
    toolButtonText: { color: gold, fontWeight: '700', fontSize: 15, backgroundColor: 'transparent' },
    toolButtonTextLg: { fontSize: 18, backgroundColor: 'transparent' },
    fontPickerWrap: { position: 'relative', marginLeft: 8, zIndex: 40, backgroundColor: 'transparent' },
    fontTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: gold,
      backgroundColor: theme.isDarkMode ? '#241e14' : '#fffaf0',
      minWidth: 170,
    },
    fontTriggerText: { color: theme.colors.text, flex: 1, backgroundColor: 'transparent' },
    fontChevron: { color: gold, marginLeft: 8, backgroundColor: 'transparent' },
    fontMenu: {
      position: 'absolute',
      top: 40,
      left: 0,
      minWidth: 220,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? '#555' : '#d9c89a',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#fffdf8',
      paddingVertical: 6,
      zIndex: 50,
      ...(Platform.OS === 'web' ? { boxShadow: '0 8px 24px rgba(0,0,0,0.22)' } : { elevation: 8 }),
    },
    fontMenuItem: { paddingHorizontal: 14, paddingVertical: 10, backgroundColor: 'transparent' },
    fontMenuItemActive: { backgroundColor: theme.isDarkMode ? '#2a2418' : '#f4e6c0' },
    fontMenuItemText: { color: theme.colors.text, backgroundColor: 'transparent' },
    fontMenuItemTextActive: { color: gold, fontWeight: '700', backgroundColor: 'transparent' },
    content: { flex: 1, flexDirection: 'row', minHeight: 0, minWidth: 0, overflow: 'hidden', zIndex: 1 },
    leftMenu: {
      flexShrink: 0,
      borderRightWidth: 1,
      borderRightColor: theme.isDarkMode ? '#333' : '#DDD',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      paddingTop: 8,
    },
    tocHeading: {
      color: gold,
      fontWeight: '700',
      fontSize: 13,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      marginHorizontal: 14,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    tocScroll: { flex: 1 },
    tocScrollContent: { paddingHorizontal: 8, paddingBottom: 24 },
    tocItem: { paddingVertical: 8, paddingHorizontal: 8, borderRadius: 8, backgroundColor: 'transparent' },
    tocItemNested: { marginLeft: 12, paddingVertical: 6 },
    tocItemActive: { backgroundColor: theme.isDarkMode ? '#2a2418' : '#efe3c4' },
    tocItemPressed: { opacity: 0.75 },
    tocText: { color: gold, backgroundColor: 'transparent' },
    tocTextTop: { fontSize: 15, fontWeight: '700' },
    tocTextSub: { fontSize: 14, fontWeight: '500' },
    tocTextActive: { fontWeight: '700', textDecorationLine: 'underline' },
    readerPane: { flex: 1, minWidth: 0, backgroundColor: '#f7f1e4' },
  });
}
